/* ==========================================================================
   Blanket Statement — woven preview simulation

   Nate's read of the process is right, and it is the basis of this:
   a jacquard photo weave has only a handful of yarn colours on the loom.
   Every other colour in the finished blanket is an illusion made by
   interleaving those yarns at thread scale, exactly the way CMYK halftones
   make continuous tone out of four inks.

   So the simulation is a halftone:
     1. resample the crop down to the blanket's real thread count
     2. ordered-dither each thread to the nearest available yarn colour
     3. scale back up with no smoothing, so each thread stays a thread
     4. lay weave structure over the top

   This is an APPROXIMATION and is labelled as one in the UI. It is not a
   proof of the finished article, and it never touches the file that is
   uploaded — Cloudinary always receives the real cropped photo.
   ========================================================================== */
(function () {
  'use strict';

  /* Six cotton yarns, as photo-weaving mills commonly run: red, yellow, blue,
     green, black, white. Values are dyed-cotton colours rather than pure RGB,
     because pure primaries make the result look like a video game. */
  /* Fallback only. The real palette is the mill's 147 engineered weave
     structures in bs-weave-palette.js; these six are what the simulator
     falls back to if that file is missing. Six colours cannot hold a
     photograph — measured, they give 40 degrees of hue error against 4 for a
     large palette — so this fallback exists to keep the page working, not to
     look right. */
  var YARNS = [
    [240, 234, 221],   // natural / white
    [ 22,  21,  19],   // black
    [181,  55,  47],   // red
    [221, 182,  74],   // yellow
    [ 46,  91, 135],   // blue
    [ 92, 138,  74]    // green
  ];
  /* The green was [76,122,79] and it made grass come out straw-yellow: a
     typical bright foliage green sat NEARER the yellow yarn than that dark
     green, so it snapped to yellow across whole fields of a photo. Tested
     against five real foliage tones, [92,138,74] keeps four of them green,
     including the dark ones that brighter greens lose to black.

     Black was [34,32,28], which is not dark enough to reach a real shadow:
     the darkest tone the preview could produce was lighter than the darks in
     an ordinary photograph, so shadows washed out to grey.

     This palette is a best guess at a mill's yarn set. It is the single
     biggest factor in how faithful the preview is, which is why `opts.palette`
     exists — put the mill's real yarn colours in and the preview stops being
     an illustration and starts being a prediction. */

  /* Nearest yarn is chosen in Oklab, not in RGB.

     Redmean was a decent cheap approximation, but it is still an RGB metric,
     and RGB distance does not track how people see hue. The measurable
     consequence was a crimson dog's tongue (hue 344) landing on the orange-red
     yarn (hue 8) and reading wrong. Oklab is close to perceptually uniform, so
     "nearest" means nearest to the eye. */
  function oklab(r, g, b) {          // takes LINEAR rgb, 0-1
    var l = 0.4122214708 * r + 0.5363325363 * g + 0.0514459929 * b;
    var m = 0.2119034982 * r + 0.6806995451 * g + 0.1073969566 * b;
    var s2 = 0.0883024619 * r + 0.2817188376 * g + 0.6299787005 * b;
    var l_ = Math.cbrt(l), m_ = Math.cbrt(m), s_ = Math.cbrt(s2);
    return [
      0.2104542553 * l_ + 0.7936177850 * m_ - 0.0040720468 * s_,
      1.9779984951 * l_ - 2.4285922050 * m_ + 0.4505937099 * s_,
      0.0259040371 * l_ + 0.7827717662 * m_ - 0.8086757660 * s_
    ];
  }

  function clamp255(v) { return v < 0 ? 0 : v > 255 ? 255 : v; }

  /* sRGB is gamma-encoded. Light is not. Averaging encoded values is the
     classic mistake that makes blends muddy and shifts their hue, and it is
     exactly what interleaved threads do NOT do — two threads side by side
     average as LIGHT. So the box filter works in linear light and converts
     back at the end. Lookup tables keep it fast. */
  var S2L = new Float32Array(256), L2S = new Uint8Array(4096);
  (function () {
    for (var i = 0; i < 256; i++) {
      var c = i / 255;
      S2L[i] = c <= 0.04045 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    }
    for (var j = 0; j < 4096; j++) {
      var l = j / 4095;
      var v = l <= 0.0031308 ? l * 12.92 : 1.055 * Math.pow(l, 1 / 2.4) - 0.055;
      L2S[j] = Math.round(clamp255(v * 255));
    }
  })();
  function toS(l) { return L2S[clamp255(Math.round(l * 4095)) > 4095 ? 4095 : (l < 0 ? 0 : Math.round(l * 4095))]; }

  /**
   * weave(source, opts) -> HTMLCanvasElement
   * source: any canvas / image already cropped to the blanket's proportions
   * opts.threadsAcross : thread count across the blanket's width
   * opts.dither        : 0-1, how hard the ordered dither pushes (default .55)
   * opts.outWidth      : display width of the returned canvas
   */
  /* Scratch canvases are reused rather than allocated per call. Each weave used
     to create five throwaway canvases — the thread grid, the output, and two
     more for the detail measurement — and changing the blanket size rebuilds the
     weave, so a customer trying sizes ran through them quickly. Desktop hides
     this; iOS does not, because a canvas holds memory the collector is in no
     hurry to reclaim, and the tab is killed rather than slowed. Reported as a
     crash after changing size a few times. */
  var _scratch = {};
  function scratchCanvas(key, w, h) {
    var c = _scratch[key];
    if (!c) { c = _scratch[key] = document.createElement('canvas'); }
    if (c.width !== w) c.width = w;
    if (c.height !== h) c.height = h;
    return c;
  }

  function weave(source, opts) {
    opts = opts || {};
    var dither = opts.dither == null ? 0.55 : opts.dither;
    var palette = (opts.palette && opts.palette.length >= 2) ? opts.palette
                : (window.BS_WEAVE_PALETTE || YARNS);
    var sw = source.naturalWidth || source.width;
    var sh = source.naturalHeight || source.height;

    /* Cap the thread grid for speed. At preview size the difference between
       1,848 real threads and the cap is invisible, and the per-thread loop is
       O(threads) with a palette search inside it. */
    var across = Math.max(24, Math.round(opts.threadsAcross || 900));
    var maxThreads = opts.maxThreads || 1400;
    if (across > maxThreads) across = maxThreads;
    var down = Math.max(16, Math.round(across * sh / sw));

    /* 1. Resample to thread resolution. */
    var grid = scratchCanvas('grid', across, down);
    var gx = grid.getContext('2d', { willReadFrequently: true });
    gx.clearRect(0, 0, across, down);
    gx.imageSmoothingEnabled = true;
    gx.imageSmoothingQuality = 'high';
    gx.drawImage(source, 0, 0, across, down);

    /* 2. Choose a yarn for every thread, in linear light, carrying the
          error forward.

          The first version picked the nearest yarn with an ordered offset.
          That is fast and it tiles, but it does not preserve the colour it is
          approximating: with only six yarns the result drifted measurably
          desaturated, and saturated colours slid in hue.

          Error diffusion fixes that by construction. Whatever a thread cannot
          represent is passed to its neighbours, so a patch of cloth averages
          to very nearly the colour asked for — which is exactly what a loom
          achieves by interleaving yarns, and why photo weaving works at all.
          The scan serpentines so the residue does not streak one way. */
    var palLin = [], palLab = [];
    for (var pi = 0; pi < palette.length; pi++) {
      var pr = S2L[palette[pi][0]], pg = S2L[palette[pi][1]], pb = S2L[palette[pi][2]];
      palLin.push([pr, pg, pb]);
      palLab.push(oklab(pr, pg, pb));
    }

    /* The palette scan is the whole cost of this function: one pass over 147
       yarns for each of up to 977,500 threads is ~144 million distance
       calculations, measured at 604ms on a desktop and several times that on a
       phone — a freeze, not a delay, and it ran again on every orientation flip.

       Two changes, neither of which alters what is woven in any way a customer
       could see:

       1. The palette lives in one flat Float32Array rather than an array of
          arrays, so a scan walks contiguous memory.
       2. The choice of yarn is memoised against a quantised colour. This is
          safe precisely because of how the dither works: the error passed on is
          the difference between the TRUE colour and the yarn chosen, so a
          slightly coarser lookup changes which yarn wins a near-tie and nothing
          else — the diffusion still corrects against the real value. */
    var palFlat = new Float32Array(palLab.length * 3);
    for (var pk = 0; pk < palLab.length; pk++) {
      palFlat[pk * 3]     = palLab[pk][0];
      palFlat[pk * 3 + 1] = palLab[pk][1];
      palFlat[pk * 3 + 2] = palLab[pk][2];
    }
    var PAL_N = palLab.length;

    var QBITS = 6, QN = 1 << QBITS, QMAX = QN - 1;
    var lut = new Int16Array(QN * QN * QN);
    lut.fill(-1);

    function nearestYarn(r, g, b) {
      var qr = r <= 0 ? 0 : (r >= 1 ? QMAX : (r * QMAX + 0.5) | 0);
      var qg = g <= 0 ? 0 : (g >= 1 ? QMAX : (g * QMAX + 0.5) | 0);
      var qb = b <= 0 ? 0 : (b >= 1 ? QMAX : (b * QMAX + 0.5) | 0);
      var key = (qr * QN + qg) * QN + qb;
      var hit = lut[key];
      if (hit !== -1) return hit;

      var lab = oklab(r < 0 ? 0 : r, g < 0 ? 0 : g, b < 0 ? 0 : b);
      var l0 = lab[0], a0 = lab[1], b0 = lab[2];
      var best = 0, bestD = Infinity;
      for (var pj = 0, pf = 0; pj < PAL_N; pj++, pf += 3) {
        var dl = l0 - palFlat[pf], da = a0 - palFlat[pf + 1], db2 = b0 - palFlat[pf + 2];
        var dist2 = dl * dl + da * da + db2 * db2;
        if (dist2 < bestD) { bestD = dist2; best = pj; }
      }
      lut[key] = best;
      return best;
    }

    var img = gx.getImageData(0, 0, across, down);
    var d = img.data;

    // Working buffer in linear light, so error accumulates in real light units.
    var buf = new Float32Array(across * down * 3);
    for (var q = 0, n3 = 0; q < d.length; q += 4, n3 += 3) {
      buf[n3]     = S2L[d[q]];
      buf[n3 + 1] = S2L[d[q + 1]];
      buf[n3 + 2] = S2L[d[q + 2]];
    }

    var spread = dither;                    // 0 = hard posterise, 1 = full diffusion
    var ECLAMP = 0.22;                      // linear-light units
    function clampErr(e) { return e > ECLAMP ? ECLAMP : (e < -ECLAMP ? -ECLAMP : e); }
    for (var y = 0; y < down; y++) {
      var ltr = (y & 1) === 0;              // serpentine
      for (var xi = 0; xi < across; xi++) {
        var x = ltr ? xi : across - 1 - xi;
        var o = (y * across + x) * 3;
        var r = buf[o], g = buf[o + 1], b = buf[o + 2];

        var best = nearestYarn(r, g, b);

        var chosen = palLin[best];
        /* Clamp the error before passing it on. With only a handful of yarns
           many photographic colours sit outside what the palette can reach, and
           unclamped error on those grows without bound — it explodes into
           streaks and blotches instead of settling. Measured: full unclamped
           diffusion was worse than no diffusion at all. */
        var er = clampErr((r - chosen[0]) * spread),
            eg = clampErr((g - chosen[1]) * spread),
            eb = clampErr((b - chosen[2]) * spread);

        buf[o] = chosen[0]; buf[o + 1] = chosen[1]; buf[o + 2] = chosen[2];

        /* These four were a closure declared inside the loop — one function
           allocated per thread, nearly a million of them per weave. Written out
           inline instead; the arithmetic is identical. */
        var nx = ltr ? 1 : -1;
        var xr = x + nx, xl = x - nx, y1 = y + 1, t;
        if (xr >= 0 && xr < across) {
          t = (y * across + xr) * 3;
          buf[t] += er * 0.4375; buf[t + 1] += eg * 0.4375; buf[t + 2] += eb * 0.4375;
        }
        if (y1 < down) {
          if (xl >= 0 && xl < across) {
            t = (y1 * across + xl) * 3;
            buf[t] += er * 0.1875; buf[t + 1] += eg * 0.1875; buf[t + 2] += eb * 0.1875;
          }
          t = (y1 * across + x) * 3;
          buf[t] += er * 0.3125; buf[t + 1] += eg * 0.3125; buf[t + 2] += eb * 0.3125;
          if (xr >= 0 && xr < across) {
            t = (y1 * across + xr) * 3;
            buf[t] += er * 0.0625; buf[t + 1] += eg * 0.0625; buf[t + 2] += eb * 0.0625;
          }
        }
      }
    }

    for (var w2 = 0, m3 = 0; w2 < d.length; w2 += 4, m3 += 3) {
      d[w2]     = toS(buf[m3]);
      d[w2 + 1] = toS(buf[m3 + 1]);
      d[w2 + 2] = toS(buf[m3 + 2]);
      d[w2 + 3] = 255;
    }
    gx.putImageData(img, 0, 0);
    var gridCanvas = grid;   // weave-resolution, post-index

    /* 3. Resolve the thread grid to display resolution with an EXACT BOX
          FILTER, written out by hand.

          Three attempts got here. Nearest-neighbour aliased whenever the
          ratio was not a whole number, which is what made some sizes look
          like a waffle. Repeated halving was right in principle but never
          ran, because the thread grid is already smaller than twice the
          display. And a single smoothed drawImage samples only a handful of
          neighbours, so how much dither survived depended on the exact ratio —
          which is precisely why one blanket size looked fine and the next
          looked coarse.

          A box filter weights every source thread by how much of the output
          pixel it actually covers. It behaves identically at any ratio, so
          every size now looks the same in character. */
    var target = Math.max(120, Math.round(opts.outWidth || 1000));
    /* Never resolve finer than two threads per output pixel. Below that the
       box filter has almost nothing to average and the dither survives as
       visible grain, by an amount that varies with the ratio — which is the
       inconsistency between sizes Nate spotted. Two threads minimum makes
       every size average the same way. */
    var outW = Math.min(target, Math.floor(across / 2));
    var outH = Math.max(1, Math.round(outW * down / across));

    var srcData = gx.getImageData(0, 0, across, down).data;
    var out = scratchCanvas('out', outW, outH);
    var ox = out.getContext('2d');
    var dst = ox.createImageData(outW, outH);
    var dd = dst.data;

    var xScale = across / outW, yScale = down / outH;
    for (var oy = 0; oy < outH; oy++) {
      var y0 = oy * yScale, y1 = y0 + yScale;
      var iy0 = Math.floor(y0), iy1 = Math.min(down, Math.ceil(y1));
      for (var oxi = 0; oxi < outW; oxi++) {
        var x0 = oxi * xScale, x1 = x0 + xScale;
        var ix0 = Math.floor(x0), ix1 = Math.min(across, Math.ceil(x1));
        var r = 0, g = 0, b = 0, wsum = 0;
        for (var yy = iy0; yy < iy1; yy++) {
          var wy = Math.min(y1, yy + 1) - Math.max(y0, yy);
          if (wy <= 0) continue;
          var rowOff = yy * across;
          for (var xx = ix0; xx < ix1; xx++) {
            var wx = Math.min(x1, xx + 1) - Math.max(x0, xx);
            if (wx <= 0) continue;
            var w = wx * wy, si = (rowOff + xx) * 4;
            r += S2L[srcData[si]] * w;
            g += S2L[srcData[si + 1]] * w;
            b += S2L[srcData[si + 2]] * w;
            wsum += w;
          }
        }
        var di = (oy * outW + oxi) * 4;
        dd[di]     = wsum ? toS(r / wsum) : 0;
        dd[di + 1] = wsum ? toS(g / wsum) : 0;
        dd[di + 2] = wsum ? toS(b / wsum) : 0;
        dd[di + 3] = 255;
      }
    }
    ox.putImageData(dst, 0, 0);

    /* 4. No thread lines are drawn into this canvas. An earlier version did,
          and the grain simply became the next aliasing source. Cloth texture
          belongs at display resolution, and the customizer lays it over the
          top in CSS (.bsc__weave) where nothing resamples it. */

    out.__grid = gridCanvas;
    return out;
  }

  /* ------------------------------------------------------------------
     Fine-detail risk, from the mill's own editing rule: a line has to be at
     least TWO pixels wide on the weave grid or it is lost in the cloth.

     So the test is literally that. Take the image at weave resolution, halve
     it with a proper area average, bring it back, and difference the two.
     Whatever changes was carried by features narrower than two weave pixels —
     exactly the detail the mill says will not survive. An artist would fix this
     by hand in step three of their workflow; a customer uploading a photo
     cannot, so the least we can do is tell them which photos are at risk.
     ------------------------------------------------------------------ */
  function detailRisk(gridCanvas) {
    var W = gridCanvas.width, H = gridCanvas.height;
    if (W < 8 || H < 8) return 0;
    var hw = Math.max(1, Math.floor(W / 2)), hh = Math.max(1, Math.floor(H / 2));

    var half = scratchCanvas('half', hw, hh);
    var hx = half.getContext('2d');
    hx.imageSmoothingEnabled = true; hx.imageSmoothingQuality = 'high';
    hx.drawImage(gridCanvas, 0, 0, hw, hh);

    var back = scratchCanvas('back', W, H);
    var bx = back.getContext('2d');
    bx.imageSmoothingEnabled = true; bx.imageSmoothingQuality = 'high';
    bx.drawImage(half, 0, 0, W, H);

    var a = gridCanvas.getContext('2d').getImageData(0, 0, W, H).data;
    var b = bx.getImageData(0, 0, W, H).data;

    // Mean absolute luma difference, and the share of the image where that
    // difference is strong enough to read as lost detail rather than noise.
    var sum = 0, n = 0, strong = 0;
    for (var i = 0; i < a.length; i += 4) {
      var la = 0.299 * a[i] + 0.587 * a[i + 1] + 0.114 * a[i + 2];
      var lb = 0.299 * b[i] + 0.587 * b[i + 1] + 0.114 * b[i + 2];
      var dd = Math.abs(la - lb);
      sum += dd; n++;
      if (dd > 26) strong++;
    }
    return { mean: sum / n, strongShare: strong / n };
  }

  window.BSWeaveSim = { weave: weave, YARNS: YARNS, detailRisk: detailRisk };
})();
